import { API_CONFIG } from '../config/api';

export interface AuthCredentials {
    client_id: string;
    client_secret: string;
}

export interface AuthTokenResponse {
    token: string;
    expiresIn: number;
}

export class AuthService {
    private baseUrl: string;
    private token: string | null = null;
    private tokenExpiry: number | null = null;
    private pendingAuthPromise: Promise<AuthTokenResponse> | null = null;

    constructor() {
        this.baseUrl = `${API_CONFIG.BASE_URL_JAVA}/api/v1`;
    }

    async getAuthToken(credentials: AuthCredentials): Promise<AuthTokenResponse> {
        const url = `${this.baseUrl}/auth`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
            });

            if (!response.ok) {
                throw new Error(`Erro na autenticação: ${response.statusText}`);
            }

            const data: AuthTokenResponse = await response.json();

            this.token = data.token;
            this.tokenExpiry = Date.now() + (data.expiresIn * 1000);
            
            return data;
        } catch (error) {
            console.error('Erro ao obter token:', error);
            throw error;
        }
    }

    async getValidToken(): Promise<string> {
        if (this.token && this.tokenExpiry && Date.now() < this.tokenExpiry) {
            return this.token;
        }

        if (this.pendingAuthPromise) {
            const authResponse = await this.pendingAuthPromise;
            return authResponse.token;
        }

        const credentials: AuthCredentials = {
            client_id: process.env.REACT_APP_CLIENT_ID || '',
            client_secret: process.env.REACT_APP_CLIENT_SECRET || ''
        };
        
        if (!credentials.client_id || !credentials.client_secret) {
            throw new Error('Credenciais de autenticação não configuradas nas variáveis de ambiente');
        }
        
        this.pendingAuthPromise = this.getAuthToken(credentials);
        
        try {
            const authResponse = await this.pendingAuthPromise;
            return authResponse.token;
        } finally {
            this.pendingAuthPromise = null;
        }
    }

    clearToken(): void {
        this.token = null;
        this.tokenExpiry = null;
    }

    isTokenValid(): boolean {
        return !!(this.token && this.tokenExpiry && Date.now() < this.tokenExpiry);
    }
}

export const authService = new AuthService();
